import { requireString } from "./commandValidation";
import {
  ControlledPdfViewer,
  type SelectionRegionGroup,
} from "./pdfViewer";
import type { PdfCommand, PdfCommandEnvelope, PdfCommandResponse } from "./types";
import "./styles.css";

const DEFAULT_PDF_URL =
  import.meta.env.VITE_DEFAULT_PDF_URL ??
  "https://docs.nlr.gov/docs/fy19osti/71949.pdf";
const sessionId = crypto.randomUUID().replaceAll("-", "");
let activeDocumentId = "active-document";
let activeDocumentUrl: string | null = null;
let selectionUpdateTimeout = 0;
let pendingBookmark: Bookmark | null = null;
const bookmarks: Bookmark[] = createDefaultBookmarks();
let commandQueue: Promise<void> = Promise.resolve();

type Bookmark = {
  id: string;
  label: string;
  detail: string;
  envelope: PdfCommandEnvelope;
};
const defaultDemoCommands = [
  {
    type: "pdf.open-document",
    documentId: "active-document",
    url: DEFAULT_PDF_URL,
  },
  {
    type: "pdf.show-region",
    documentId: "active-document",
    requestId: "demo-highlight",
    pageIndex: 0,
    regions: [
      {
        coordinateSpace: "normalized-page",
        x: 0.12,
        y: 0.18,
        width: 0.48,
        height: 0.1,
      },
    ],
    behavior: {
      placement: "center",
      pulse: true,
      durationMs: 8000,
    },
  },
] satisfies PdfCommand[];

function createDefaultBookmarks(): Bookmark[] {
  return [
    createDefaultBookmark({
      id: "default-page-22",
      label: "A reduction in feedstock price to $71.26/dry shor...",
      detail: "Page 22",
      requestId: "selection-ms8th7xi-1",
      pageIndex: 21,
      region: { x: 0.1732, y: 0.4573, width: 0.4892, height: 0.0171 },
    }),
    createDefaultBookmark({
      id: "default-page-6",
      label: "Region on page 6",
      detail: "Page 6",
      requestId: "region-ms8tgs23-1",
      pageIndex: 5,
      region: { x: 0.5287, y: 0.6693, width: 0.3431, height: 0.015 },
    }),
    createDefaultBookmark({
      id: "default-page-4",
      label: "$2/gallon gasoline equivalent (GGE)",
      detail: "Page 4",
      requestId: "selection-ms8tgftz-1",
      pageIndex: 3,
      region: { x: 0.5685, y: 0.7793, width: 0.2895, height: 0.0171 },
    }),
  ];
}

function createDefaultBookmark(input: {
  id: string;
  label: string;
  detail: string;
  requestId: string;
  pageIndex: number;
  region: { x: number; y: number; width: number; height: number };
}): Bookmark {
  return {
    id: input.id,
    label: input.label,
    detail: input.detail,
    envelope: {
      commands: [
        {
          type: "pdf.open-document",
          documentId: "active-document",
          url: DEFAULT_PDF_URL,
        },
        {
          type: "pdf.show-region",
          documentId: "active-document",
          requestId: input.requestId,
          pageIndex: input.pageIndex,
          regions: [
            {
              coordinateSpace: "normalized-page",
              ...input.region,
            },
          ],
          behavior: {
            placement: "center",
            smooth: false,
            pulse: true,
            durationMs: 0,
          },
        },
      ],
    },
  };
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing app root.");

app.innerHTML = `
  <div class="app-shell">
    <header id="toolbar" class="toolbar" aria-label="Application status" hidden>
      <div id="stalePageNotice" class="stale-page-notice" role="status" hidden>
        <span>A newer version of Peedy is available.</span>
        <button id="reloadButton" type="button">Reload Peedy</button>
      </div>
    </header>

    <main class="viewer-layout">
      <aside class="command-panel" aria-label="Command console">
        <div class="panel-heading">
          <h1>Peedy</h1>
          <div class="session-badge">
            <span>Session</span>
            <code id="sessionId"></code>
          </div>
        </div>

        <div class="panel-tabs" role="tablist" aria-label="Panel views">
          <button id="bookmarksTab" class="panel-tab is-active" type="button"
            role="tab" aria-selected="true" aria-controls="bookmarksPanel">
            Bookmarks
          </button>
          <button id="commandTab" class="panel-tab" type="button"
            role="tab" aria-selected="false" aria-controls="commandPanel">
            Command
          </button>
        </div>

        <section id="bookmarksPanel" class="tab-panel bookmarks-panel" role="tabpanel"
          aria-labelledby="bookmarksTab">
          <div class="bookmark-actions">
            <button id="addBookmarkButton" class="primary-button" type="button" disabled>
              Add Bookmark
            </button>
            <button id="exportBookmarksButton" type="button" disabled>
              Export Bookmarks
            </button>
          </div>
          <p id="bookmarkHint">Select text or drag over a region to create a bookmark.</p>
          <div id="bookmarkList" class="bookmark-list"></div>
        </section>

        <section id="commandPanel" class="tab-panel command-tab-panel" role="tabpanel"
          aria-labelledby="commandTab" hidden>
          <button id="copyCurlButton" class="primary-button" type="button">Copy Load Curl</button>
          <p>
            Send <code>pdf.show-region</code> commands with stable document
            coordinates.
          </p>
          <textarea id="commandInput" spellcheck="false" aria-label="Command JSON"></textarea>
          <button id="sendCommandButton" class="primary-button" type="button">Send Command</button>
        </section>
        <pre id="statusLog" aria-live="polite"></pre>
      </aside>

      <section class="viewer" aria-label="PDF document">
        <div id="pageHost" class="page-host"></div>
      </section>
    </main>
  </div>
`;

const elements = {
  pageHost: mustFind<HTMLElement>("#pageHost"),
  viewer: mustFind<HTMLElement>(".viewer"),
  toolbar: mustFind<HTMLElement>("#toolbar"),
  stalePageNotice: mustFind<HTMLElement>("#stalePageNotice"),
  reloadButton: mustFind<HTMLButtonElement>("#reloadButton"),
  bookmarksTab: mustFind<HTMLButtonElement>("#bookmarksTab"),
  commandTab: mustFind<HTMLButtonElement>("#commandTab"),
  bookmarksPanel: mustFind<HTMLElement>("#bookmarksPanel"),
  commandPanel: mustFind<HTMLElement>("#commandPanel"),
  addBookmarkButton: mustFind<HTMLButtonElement>("#addBookmarkButton"),
  exportBookmarksButton: mustFind<HTMLButtonElement>("#exportBookmarksButton"),
  bookmarkHint: mustFind<HTMLElement>("#bookmarkHint"),
  bookmarkList: mustFind<HTMLElement>("#bookmarkList"),
  copyCurlButton: mustFind<HTMLButtonElement>("#copyCurlButton"),
  commandInput: mustFind<HTMLTextAreaElement>("#commandInput"),
  sendCommandButton: mustFind<HTMLButtonElement>("#sendCommandButton"),
  sessionId: mustFind<HTMLElement>("#sessionId"),
  statusLog: mustFind<HTMLPreElement>("#statusLog"),
};

elements.sessionId.textContent = sessionId;
removeReloadMarker();

elements.commandInput.value = JSON.stringify(
  { commands: defaultDemoCommands },
  null,
  2,
);
renderBookmarks();
elements.exportBookmarksButton.disabled = bookmarks.length === 0;

const viewer = new ControlledPdfViewer(
  {
    pageHost: elements.pageHost,
    scrollContainer: elements.viewer,
  },
  log,
  updateCommandFromRegion,
);

const resizeObserver = new ResizeObserver(() => {
  window.requestAnimationFrame(() => {
    viewer.refitWidth().catch((error: unknown) => logError(error));
  });
});
resizeObserver.observe(elements.viewer);

elements.pageHost.innerHTML = `
  <div class="page-placeholder">Waiting for a document command.</div>
`;

window.controlledPdfViewer = {
  sessionId,
  openDocument: (input) => viewer.openDocument(input),
  showRegions: (command) => viewer.showRegions(command),
  clearHighlight: (requestId) => viewer.clearHighlight(requestId),
  clearAllHighlights: () => viewer.clearAllHighlights(),
  getSelectionRegionGroups: () => viewer.getSelectionRegionGroups(),
};

elements.reloadButton.addEventListener("click", reloadLatestBuild);
elements.bookmarksTab.addEventListener("click", () => selectPanelTab("bookmarks"));
elements.commandTab.addEventListener("click", () => selectPanelTab("command"));
elements.addBookmarkButton.addEventListener("click", addPendingBookmark);
elements.exportBookmarksButton.addEventListener("click", () => {
  exportBookmarks().catch((error: unknown) => logError(error));
});
elements.copyCurlButton.addEventListener("click", () => {
  copyCurlCommand().catch((error: unknown) => logError(error));
});

elements.sendCommandButton.addEventListener("click", () => {
  try {
    enqueueCommandEnvelope(
      JSON.parse(elements.commandInput.value) as PdfCommandEnvelope,
    );
  } catch (error) {
    logError(error);
  }
});

window.addEventListener("message", (event: MessageEvent<unknown>) => {
  if (!isAllowedMessageOrigin(event.origin)) return;
  if (!event.data || typeof event.data !== "object") return;

  enqueueCommandEnvelope(event.data as PdfCommandEnvelope, (response) => {
    event.source?.postMessage(response, { targetOrigin: event.origin });
  });
});

document.addEventListener("selectionchange", scheduleSelectionBookmarkUpdate);
connectCommandStream();
startStalePageDetector();
enqueueCommandEnvelope({ commands: defaultDemoCommands }).catch(
  (error: unknown) => logError(error),
);

async function handleCommand(
  command: PdfCommand,
  respond?: (response: PdfCommandResponse) => void,
): Promise<void> {
  try {
    if (command.type === "pdf.open-document") {
      const documentUrl = requireString(command.url, "url");
      await viewer.openDocument({
        documentId: command.documentId,
        url: proxiedPdfUrl(documentUrl),
      });
      activeDocumentId = command.documentId ?? "active-document";
      activeDocumentUrl = documentUrl;
    } else if (command.type === "pdf.show-region") {
      await viewer.showRegions(command);
    } else if (command.type === "pdf.clear-highlight") {
      viewer.clearHighlight(requireString(command.requestId, "requestId"));
    } else if (command.type === "pdf.clear-all-highlights") {
      viewer.clearAllHighlights();
    } else {
      const exhaustive: never = command;
      throw new Error(`Unsupported command: ${JSON.stringify(exhaustive)}`);
    }

    respond?.({
      type: `${command.type}.completed`,
      requestId: "requestId" in command ? command.requestId : undefined,
      documentId: "documentId" in command ? command.documentId : undefined,
    });
  } catch (error) {
    const message = getErrorMessage(error);
    log(message, "error");
    respond?.({
      type: `${command.type}.failed`,
      requestId: "requestId" in command ? command.requestId : undefined,
      message,
    });
  }
}

function scheduleSelectionBookmarkUpdate(): void {
  window.clearTimeout(selectionUpdateTimeout);
  selectionUpdateTimeout = window.setTimeout(updateCommandFromSelection, 160);
}

function updateCommandFromSelection(): void {
  if (!activeDocumentUrl) return;
  if (document.activeElement === elements.commandInput) return;

  const groups = viewer.getSelectionRegionGroups();
  if (groups.length === 0) return;

  viewer.clearAllHighlights(false);
  const selectedText = window.getSelection()?.toString().trim() ?? "";
  updateBookmarkCommand(
    groups,
    "selection",
    "Selection ready to bookmark.",
    bookmarkLabel(selectedText),
  );
}

function updateCommandFromRegion(group: SelectionRegionGroup): void {
  viewer.clearAllHighlights(false);
  const showCommands = updateBookmarkCommand(
    [group],
    "region",
    "Region ready to bookmark.",
    `Region on page ${group.pageIndex + 1}`,
  );
  const previewCommand = showCommands[0];
  if (!previewCommand) return;

  viewer.showRegions({
    ...previewCommand,
    behavior: {
      ...previewCommand.behavior,
      scroll: "none",
      pulse: false,
    },
  }).catch((error: unknown) => logError(error));
}

function updateBookmarkCommand(
  groups: SelectionRegionGroup[],
  requestPrefix: string,
  message: string,
  label: string,
): Array<Extract<PdfCommand, { type: "pdf.show-region" }>> {
  if (!activeDocumentUrl) return [];

  const bookmarkId = `${requestPrefix}-${Date.now().toString(36)}`;
  const showCommands = groups.map((group, index) => ({
    type: "pdf.show-region" as const,
    documentId: activeDocumentId,
    requestId: `${bookmarkId}-${index + 1}`,
    pageIndex: group.pageIndex,
    regions: group.regions,
    behavior: {
      placement: "center" as const,
      smooth: false,
      pulse: true,
      durationMs: 0,
    },
  }));
  const commands: PdfCommand[] = [
    {
      type: "pdf.open-document",
      documentId: activeDocumentId,
      url: activeDocumentUrl,
    },
    ...showCommands,
  ];

  const envelope = { commands } satisfies PdfCommandEnvelope;
  elements.commandInput.value = JSON.stringify(envelope, null, 2);
  pendingBookmark = {
    id: bookmarkId,
    label,
    detail: bookmarkDetail(groups),
    envelope,
  };
  elements.addBookmarkButton.disabled = false;
  elements.bookmarkHint.textContent = `${label} is ready to add.`;
  log(message);
  return showCommands;
}

function bookmarkLabel(selectedText: string): string {
  const compact = selectedText.replace(/\s+/g, " ").trim();
  if (!compact) return "Text selection";
  return compact.length > 52 ? `${compact.slice(0, 49)}...` : compact;
}

function bookmarkDetail(groups: SelectionRegionGroup[]): string {
  const firstPage = groups[0]?.pageIndex ?? 0;
  const lastPage = groups.at(-1)?.pageIndex ?? firstPage;
  return firstPage === lastPage
    ? `Page ${firstPage + 1}`
    : `Pages ${firstPage + 1}-${lastPage + 1}`;
}

function addPendingBookmark(): void {
  if (!pendingBookmark) return;

  bookmarks.unshift(pendingBookmark);
  const added = pendingBookmark;
  pendingBookmark = null;
  elements.addBookmarkButton.disabled = true;
  elements.exportBookmarksButton.disabled = false;
  elements.bookmarkHint.textContent = "Select text or drag over a region to create a bookmark.";
  renderBookmarks();
  log(`Added bookmark: ${added.label}`);
}

async function exportBookmarks(): Promise<void> {
  if (bookmarks.length === 0) return;

  const exported = {
    bookmarks: bookmarks.map(({ label, detail, envelope }) => ({
      label,
      detail,
      command: envelope,
    })),
  };
  await navigator.clipboard.writeText(JSON.stringify(exported, null, 2));
  log(`Exported ${bookmarks.length} bookmark(s) as JSON.`);
}

function renderBookmarks(): void {
  elements.bookmarkList.replaceChildren();

  if (bookmarks.length === 0) {
    const empty = document.createElement("p");
    empty.className = "bookmark-empty";
    empty.textContent = "No bookmarks yet.";
    elements.bookmarkList.append(empty);
    return;
  }

  for (const bookmark of bookmarks) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "bookmark-entry";
    button.dataset.bookmarkId = bookmark.id;
    button.innerHTML = `<span>${escapeHtml(bookmark.label)}</span><small>${bookmark.detail}</small>`;
    button.addEventListener("click", () => {
      elements.commandInput.value = JSON.stringify(bookmark.envelope, null, 2);
      enqueueCommandEnvelope(bookmark.envelope).catch((error: unknown) => logError(error));
      log(`Opened bookmark: ${bookmark.label}`);
    });
    elements.bookmarkList.append(button);
  }
}

function selectPanelTab(tab: "bookmarks" | "command"): void {
  const showBookmarks = tab === "bookmarks";
  elements.bookmarksPanel.hidden = !showBookmarks;
  elements.commandPanel.hidden = showBookmarks;
  elements.bookmarksTab.classList.toggle("is-active", showBookmarks);
  elements.commandTab.classList.toggle("is-active", !showBookmarks);
  elements.bookmarksTab.setAttribute("aria-selected", String(showBookmarks));
  elements.commandTab.setAttribute("aria-selected", String(!showBookmarks));
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

async function handleCommandEnvelope(
  envelope: PdfCommandEnvelope,
  respond?: (response: PdfCommandResponse) => void,
): Promise<void> {
  if ("commands" in envelope) {
    for (const command of envelope.commands) {
      await handleCommand(command, respond);
    }
    return;
  }

  await handleCommand(envelope, respond);
}

function enqueueCommandEnvelope(
  envelope: PdfCommandEnvelope,
  respond?: (response: PdfCommandResponse) => void,
): Promise<void> {
  const execution = commandQueue.then(() =>
    handleCommandEnvelope(envelope, respond),
  );
  commandQueue = execution.catch(() => undefined);
  return execution;
}

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
}

function log(message: string, level: "info" | "error" = "info"): void {
  const line = `[${new Date().toLocaleTimeString()}] ${message}`;
  elements.statusLog.textContent = `${line}\n${elements.statusLog.textContent}`;
  elements.statusLog.classList.toggle("status-error", level === "error");
}

function logError(error: unknown): void {
  log(getErrorMessage(error), "error");
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error.";
}

function isAllowedMessageOrigin(origin: string): boolean {
  if (origin === window.location.origin) return true;

  const configuredOrigins = import.meta.env.VITE_CONTROLLER_ORIGINS;
  if (typeof configuredOrigins !== "string" || configuredOrigins.length === 0) {
    return false;
  }

  return configuredOrigins
    .split(",")
    .map((value) => value.trim())
    .includes(origin);
}

function proxiedPdfUrl(url: string): string {
  const parsedUrl = new URL(url, window.location.href);
  if (parsedUrl.origin === window.location.origin && parsedUrl.pathname === "/api/pdf") {
    return parsedUrl.href;
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    throw new Error("Only http and https PDF URLs can be proxied.");
  }

  const proxyUrl = new URL("/api/pdf", window.location.origin);
  proxyUrl.searchParams.set("url", parsedUrl.href);
  return proxyUrl.href;
}

function connectCommandStream(): void {
  const events = new EventSource(`/api/sessions/${sessionId}/events`);

  events.addEventListener("ready", () => {
    log(`Session ${sessionId} ready for remote commands.`);
  });

  events.addEventListener("command", (event) => {
    try {
      const payload = JSON.parse(event.data) as { command?: PdfCommandEnvelope };
      if (!payload.command) throw new Error("Missing command payload.");

      enqueueCommandEnvelope(payload.command);
    } catch (error) {
      logError(error);
    }
  });

  events.addEventListener("error", () => {
    log("Remote command stream disconnected; reconnecting automatically.", "error");
  });
}

async function copyCurlCommand(): Promise<void> {
  const endpoint = new URL(
    `/api/sessions/${sessionId}/commands`,
    window.location.origin,
  );
  const envelope = JSON.parse(elements.commandInput.value) as PdfCommandEnvelope;
  const body = JSON.stringify(envelope);
  const command = [
    `curl -X POST '${endpoint.href}'`,
    `  -H 'Content-Type: application/json'`,
    `  -d '${shellSingleQuote(body)}'`,
  ].join(" \\\n");

  await navigator.clipboard.writeText(command);
  log("Copied curl command.");
}

function shellSingleQuote(value: string): string {
  return value.replaceAll("'", "'\"'\"'");
}

function startStalePageDetector(): void {
  checkForStalePage();
  window.setInterval(checkForStalePage, 30_000);
  window.addEventListener("focus", checkForStalePage);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForStalePage();
  });
}

async function checkForStalePage(): Promise<void> {
  try {
    const versionUrl = new URL("/version.json", window.location.origin);
    versionUrl.searchParams.set("check", Date.now().toString());
    const response = await fetch(versionUrl, { cache: "no-store" });
    if (!response.ok) return;

    const version = (await response.json()) as { buildId?: unknown };
    if (
      typeof version.buildId === "string" &&
      version.buildId !== __PEEDY_BUILD_ID__
    ) {
      elements.stalePageNotice.dataset.latestBuildId = version.buildId;
      elements.stalePageNotice.hidden = false;
      elements.toolbar.hidden = false;
    }
  } catch {
    // A failed update check must not interrupt the viewer.
  }
}

function reloadLatestBuild(): void {
  const latestBuildId = elements.stalePageNotice.dataset.latestBuildId;
  const reloadUrl = new URL(window.location.href);
  reloadUrl.searchParams.set(
    "peedyBuild",
    latestBuildId ?? Date.now().toString(),
  );
  window.location.replace(reloadUrl);
}

function removeReloadMarker(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("peedyBuild")) return;

  url.searchParams.delete("peedyBuild");
  window.history.replaceState(null, "", url);
}
